/**
 * @fileoverview HelpPopover — Blueprint v6 Popover wrapper that renders a
 * data-driven vertical bp6-menu of help-menu groups separated by
 * HelpMenuDivider. Each group's entries render as either HelpMenuItem (default)
 * or HelpMenuExternalItem (when `entry.external === true`). The popover
 * anchors right of its trigger and uses Blueprint's non-minimal style so the
 * arrow renders by default. Blueprint v6 also restores focus to the trigger
 * on close — do NOT pass `enforceFocus={false}` here.
 *
 * Foundry mapping: `bp6-popover-placement-right` help menu in the
 * workspace-sidebar footer.
 */

import { Menu, Popover } from "@blueprintjs/core";
import { Fragment, type ReactElement, type ReactNode } from "react";
import { HelpButton } from "./help-button";
import { HelpMenuDivider } from "./help-menu-divider";
import { HelpMenuExternalItem } from "./help-menu-external-item";
import { HelpMenuItem } from "./help-menu-item";

export interface HelpMenuEntry {
  readonly id: string;
  readonly label: string;
  /** A rendered icon element — e.g. `<Icon icon="learning" />` or
   *  `<AipLogoIcon />`. Typed as `ReactElement` to match Blueprint's
   *  `MaybeElement` shape (JSX.Element | false | null | undefined). */
  readonly icon: ReactElement;
  readonly href?: string;
  readonly external?: boolean;
  readonly onClick?: () => void;
}

export interface HelpMenuGroup {
  readonly id: string;
  readonly items: readonly HelpMenuEntry[];
}

export interface HelpPopoverProps {
  readonly groups: readonly HelpMenuGroup[];
  readonly trigger?: ReactNode;
  /** Default placement="right" — anchored to the right of the rail-footer trigger. */
  readonly placement?: "right" | "right-end" | "right-start" | "top" | "bottom";
  readonly popoverClassName?: string;
  /** When true, the Popover's target wrapper becomes display:block / width:100%
   *  so a rail-item-styled trigger spans the full rail width. Off by default to
   *  preserve the generic icon-button behaviour. */
  readonly fill?: boolean;
}

export function HelpPopover({
  groups,
  trigger,
  placement = "right",
  popoverClassName,
  fill = false,
}: HelpPopoverProps): ReactElement {
  return (
    <Popover data-testid="help-popover"
      placement={placement}
      minimal={false}
      fill={fill}
      popoverClassName={["bp-help-menu-popover", popoverClassName].filter(Boolean).join(" ")}
      content={
        <Menu>
          {groups.map((group, gi) => (
            <Fragment key={group.id}>
              {gi > 0 && <HelpMenuDivider />}
              {group.items.map((entry) =>
                entry.external && entry.href ? (
                  <HelpMenuExternalItem
                    key={entry.id}
                    label={entry.label}
                    icon={entry.icon}
                    href={entry.href}
                  />
                ) : (
                  <HelpMenuItem
                    key={entry.id}
                    label={entry.label}
                    icon={entry.icon}
                    onClick={entry.onClick}
                    href={entry.href}
                  />
                ),
              )}
            </Fragment>
          ))}
        </Menu>
      }
    >
      {trigger ?? <HelpButton />}
    </Popover>
  );
}
